<template>
  <div
    :class="ns.e('virtual-container')"
    :style="{
      height: `${totalSize}px`,
      width: `${tableWidth}px`,
      minWidth: `${tableWidth}px`,
      maxWidth: `${tableWidth}px`,
    }"
  >
    <grid-virtual-row
      v-for="virtualRow in virtualRows"
      :key="rowKey(tableData[virtualRow.index], virtualRow.index)"
      :row="tableData[virtualRow.index]"
      :index="virtualRow.index"
      :virtual-start="virtualRow.start"
      :fixed-type="fixedType"
      :overflow-x="overflowX"
      :columns="columns"
      :row-height="rowHeight"
      :table-width="tableWidth"
      :seq-label="seqIndex(virtualRow.index)"
      @click="onRowClick"
      @dblclick="onRowDblClick"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { useNamespace } from '@zoryn/hooks'
import { beginRowEdit } from '../methods/edit'
import { resolveGridDblClickEdit } from '../column/meta'
import { getRowId } from '../utils/row-id'
import { isInfiniteScrollEnabled } from '../utils/infinite-scroll'
import GridVirtualRow from './grid-virtual-row.vue'
import type { DataGrid, GridColumnInfo } from '../types'
import type { GridFixedType } from '../utils/fixed-column'

const props = defineProps<{
  fixedType: GridFixedType
  overflowX: boolean
  columns: GridColumnInfo[]
  virtualRows: Array<{ index: number; start: number }>
  totalSize: number
  rowHeight: number
  tableWidth: number
}>()

const ns = useNamespace('grid')
const $grid = inject('$grid') as DataGrid
const { reactData, props: gridProps } = $grid

const columns = computed(() => props.columns)
const tableData = computed(() => reactData.tableData)
const dblClickEditEnabled = computed(() =>
  resolveGridDblClickEdit(gridProps, $grid.options)
)

function rowKey(row: Record<string, unknown>, index: number) {
  return getRowId(row, index)
}

function seqIndex(index: number) {
  // 无限滚动时 tableData 已是累计行，序号从 1 连续递增
  if (isInfiniteScrollEnabled($grid.options)) {
    return index + 1
  }
  return (reactData.currentPage - 1) * reactData.pageSize + index + 1
}

function onRowClick(row: Record<string, unknown>) {
  $grid.setCurrentRow(row)
}

function onRowDblClick(row: Record<string, unknown>) {
  if (!dblClickEditEnabled.value) {
    return
  }
  if (!beginRowEdit($grid, row)) {
    return
  }
  $grid.setCurrentRow(row)
}
</script>
